import { Component, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { DxButtonModule } from 'devextreme-angular';
import { EditFacilityModalModule } from '../edit-facility-modal/edit-facility-modal.component';
import { LumpPaymentLinksModule } from '../../../pages/orders/lump-payment-links/lump-payment-links.module';
import { Subscription } from 'rxjs';
import { SessionService } from '../../services/session.service';
import { UserService } from '../../services/user/user.service';
import { FacilityDocumentsService } from '../../services/facility-documents.service';
import { ScreenService } from '../../services/screen.service';
import { FeatureFlagsService } from '../../services/featureFlags.service';
import { CompanyService } from '../../services/user/company.service';
import { PastDueService, PastDueSummary } from '../../services/pastDue.service';
import { paymentOptionLabel } from '../../helpers/payment-option';

@Component({
  selector: 'app-facility-header',
  templateUrl: './facility-header.component.html',
  styleUrls: ['./facility-header.component.scss']
})
export class FacilityHeaderComponent implements OnInit, OnDestroy {
  @Input() primaryFacility: any;
  
  expiredMessage = '';
  expiredDocumentType = '';
  expiredLicense = false;
  isCollapsed = false;
  subs: Subscription = new Subscription();
  
  showEditFacilityModal = false;
  editFacilityId: number;
  editFacilityFormData: any;

  user: any;
  company: any;
  termsWarningVisible = false;
  lumpVisible = false;
  lumpEnabled = false;

  get paymentTypeLabel(): string {
    return paymentOptionLabel(this.company?.payment_option);
  }

  pastDueSummary: PastDueSummary | null = null;
  pastDueCount = 0;
  pastDueAmount = 0;

  constructor(
    public router: Router,
    private sessionService: SessionService,
    private userService: UserService,
    private facilityDocumentsService: FacilityDocumentsService,
    private screenService: ScreenService,
    public featureFlagsService: FeatureFlagsService,
    private companyService: CompanyService,
    public pastDue: PastDueService
  ) {}

  ngOnInit() {
    this.lumpEnabled = !!this.featureFlagsService.getByEnv('process', 'lump_payments');
    this.getProfileInformation()
    if (this.primaryFacility) {
      this.checkLicenses();
      this.validateFacilityCompany();
      this.checkPastDue();
    }
    this.watchPrimaryFacilityUpdatedTrigger();
    this.setCollapsed(this.isSmall);
  }

  get isSalesRepresentative(): boolean {
    return !!(this.user?.is_sales_representative || this.user?.is_customer_representative);
  }

  // Rep-facing entry point to the bulk-payment builder (customer + sales
  // reps); admins use the per-row grid buttons, buyers the orders toolbar.
  get showBulkPayment(): boolean {
    return this.lumpEnabled && !!(
      this.user?.is_customer_representative || this.user?.is_sales_representative
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  get isSmall() {
    return this.screenService.sizes['screen-x-small'] || this.screenService.sizes['screen-small'];
  }

  setCollapsed(value: boolean) {
    this.isCollapsed = value;
  }

  get canEditFacility() {
    if (this.user?.is_sales_representative || this.user?.is_customer_representative) {
      return this.featureFlagsService.getByEnv('process', 'allow_rep_facility_edit');
    }
    return this.featureFlagsService.getByEnv('process', 'allow_customer_facility_edit');
  }

  watchPrimaryFacilityUpdatedTrigger() {
    const s = this.sessionService.primaryFacilityUpdatedTrigger.subscribe(
      () => this.getProfileInformation(),
    );
    this.subs.add(s);
  }

  getProfileInformation() {
    this.userService.getMe().subscribe(res => {
      this.user = res.data;
      this.primaryFacility = res.data.primary_facility;
      this.validateFacilityCompany();
      this.checkLicenses();
      this.checkPastDue();
    });
  }

  checkPastDue() {
    this.pastDueSummary = null;
    this.pastDueCount = 0;
    this.pastDueAmount = 0;
    if (!this.pastDue.enabled() || !this.isSalesRepresentative || !this.primaryFacility?.id) {
      return;
    }
    const s = this.pastDue.getFacilitySummary(this.primaryFacility.id).subscribe(summary => {
      this.pastDueSummary = summary;
      this.pastDueCount = summary.count;
      this.pastDueAmount = summary.total_amount;
    });
    this.subs.add(s);
  }

  validateFacilityCompany() {
    if (this.primaryFacility?.company_id) {
      this.companyService
        .companyProfileInformation(this.primaryFacility.company_id, false)
        .subscribe((res) => {
          this.company = res.data;
          this.termsWarningVisible =
            !(this.company.agreed_to_terms &&
            this.company.mi_terms_days &&
            this.company.user_agreed);
        });
    }
  }

  async checkLicenses() {
    const status = await this.facilityDocumentsService.getStatus(
      this.primaryFacility,
    );
    this.expiredLicense = status.expiredLicense;
    this.expiredMessage = status.expiredMessage;
  }

  toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
  }

  openEditFacilityModal() {
    this.editFacilityId = this.primaryFacility.id;
    this.editFacilityFormData = {
      name: this.primaryFacility.name,
      phone: this.primaryFacility.phone || '',
      delivery_address: {
        line_1: this.primaryFacility.delivery_address?.line_1 || '',
        line_2: this.primaryFacility.delivery_address?.line_2 || '',
        city: this.primaryFacility.delivery_address?.city || '',
        state: this.primaryFacility.delivery_address?.state || '',
        postal_code: this.primaryFacility.delivery_address?.postal_code || ''
      }
    };
    this.showEditFacilityModal = true;
  }
}

@NgModule({
  declarations: [FacilityHeaderComponent],
  imports: [
    CommonModule,
    RouterModule,
    DxButtonModule,
    EditFacilityModalModule,
    LumpPaymentLinksModule
  ],
  exports: [FacilityHeaderComponent]
})
export class FacilityHeaderModule {}

